import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  ModalFooter,
  Button,
  ModalBody,
  Text,
  Input,
  TextArea,
  Spacer,
  CardSelector,
} from '@flexisaf/flexibull2';
import PropTypes from 'prop-types';
import Theme from 'utils/theme';
import { useSelector, useDispatch } from 'react-redux';
import { selectSendingEmail } from 'redux/applicants/selector';
import {
  sendEmailToApplicants,
  sendSmsToApplicants,
} from 'redux/applicants/actions';
import RichTextEditor from 'components/styles/editor.style';
import { DRAFT_STEP } from 'components/WorkflowSteps/utils';

const CHANNEL_EMAIL = 'email';
const CHANNEL_SMS = 'sms';

const isUuid = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );

export const SendMailModal = ({
  emailState,
  handleModal,
  application,
  callBack,
}) => {
  const dispatch = useDispatch();
  const { open, stage, stageName, applicants, singleApplicant } = emailState;
  const [sendTo, setSendTo] = useState('');
  const [channel, setChannel] = useState(CHANNEL_EMAIL);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState({
    title: '',
    body: '',
  });

  const sending = useSelector(selectSendingEmail);
  const isSms = channel === CHANNEL_SMS;
  const isDraftStage =
    stage === DRAFT_STEP.id || stageName === DRAFT_STEP.name;

  const closeModal = () => {
    handleModal();
    setBody('');
    setSubject('');
    setSendTo('');
    setChannel(CHANNEL_EMAIL);
    setError({
      title: '',
      body: '',
    });
  };

  const buildAudiencePayload = () => {
    const payload = {
      applicationId: application?.id,
    };

    if (sendTo === 'all') {
      payload.candidateIds = [];
      if (isDraftStage) {
        payload.status = 'INCOMPLETE';
      } else if (isUuid(stage)) {
        payload.stageId = stage;
      }
    } else {
      payload.candidateIds =
        applicants?.map((candidate) => candidate.id) || [];
    }

    return payload;
  };

  const submit = () => {
    if (isSms) {
      if (!body?.trim()) {
        setError({
          ...error,
          body: 'Kindly enter the SMS message',
        });
        return;
      }
      const data = {
        ...buildAudiencePayload(),
        message: body.trim(),
      };
      dispatch(
        sendSmsToApplicants(data, () => {
          closeModal();
          if (callBack) {
            callBack();
          }
        })
      );
      return;
    }

    if (!subject) {
      setError({
        ...error,
        title: 'Kindly enter the subject of your email',
      });
      return;
    }
    if (!body) {
      setError({
        ...error,
        body: 'Kindly enter the body of your email',
      });
      return;
    }

    const data = {
      ...buildAudiencePayload(),
      message: body,
      subject,
      stage: isUuid(stage) ? stage : undefined,
    };
    dispatch(
      sendEmailToApplicants(data, () => {
        closeModal();
        if (callBack) {
          callBack();
        }
      })
    );
  };

  useEffect(() => {
    if (open) {
      if (applicants.length > 0) {
        setSendTo('selected');
      } else {
        setSendTo('all');
      }
      setChannel(CHANNEL_EMAIL);
      setBody('');
      setSubject('');
      setError({ title: '', body: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = isSms ? Boolean(body?.trim()) : Boolean(subject && body);

  return (
    <>
      <Modal open={open}>
        <ModalBody width="950px">
          <Box pad="50px">
            <Text block size="25px">
              Send Notification
            </Text>
            <Spacer />
            <Text block>
              Choose how to notify applicants, then enter your message.
            </Text>

            <Box margin="20px 0 0">
              <Text bold size="14px">
                Send Via
              </Text>
              <Box margin="5px -5px 0" display="flex">
                <CardSelector
                  style={{ width: '100%' }}
                  name="channel-email"
                  value="email"
                  view="label"
                  checked={channel === CHANNEL_EMAIL}
                  onChange={() => {
                    setChannel(CHANNEL_EMAIL);
                    setBody('');
                    setError({ title: '', body: '' });
                  }}
                  label={
                    <Text>
                      <i style={{ fontSize: '14px' }} className="saf-mail" />{' '}
                      Email
                    </Text>
                  }
                  type="checkbox"
                  description="Send as email with subject and rich text body"
                />
                <CardSelector
                  style={{ width: '100%' }}
                  name="channel-sms"
                  value="sms"
                  view="label"
                  checked={channel === CHANNEL_SMS}
                  onChange={() => {
                    setChannel(CHANNEL_SMS);
                    setSubject('');
                    setBody('');
                    setError({ title: '', body: '' });
                  }}
                  label={
                    <Text>
                      <i className="saf-sms" /> SMS Message
                    </Text>
                  }
                  type="checkbox"
                  description="Send as SMS text message"
                />
              </Box>

              {!singleApplicant ? (
                <Box margin="0 -5px" pad="15px 0">
                  <CardSelector
                    name="select"
                    value="1"
                    view="label"
                    label={`Send ${isSms ? 'SMS' : 'Email'} to all Applicant(s)`}
                    description={`Send ${
                      isSms ? 'SMS' : 'email'
                    } to all applicants within ${stageName} stage`}
                    type="checkbox"
                    checked={sendTo === 'all'}
                    onChange={() => setSendTo('all')}
                  />
                  {applicants.length > 0 && (
                    <CardSelector
                      name="select"
                      value="1"
                      view="label"
                      label={`Send ${
                        isSms ? 'SMS' : 'Email'
                      } to selected Applicant(s)`}
                      description={`Send ${
                        isSms ? 'SMS' : 'email'
                      } to selected applicant(s) within ${stageName} stage`}
                      type="checkbox"
                      checked={sendTo === 'selected'}
                      onChange={() => setSendTo('selected')}
                    />
                  )}
                </Box>
              ) : (
                <Box margin="0" pad="15px 0">
                  <Text block>
                    <strong>
                      Sending {isSms ? 'SMS' : 'Email'} to:
                    </strong>{' '}
                    {applicants[0]?.firstName} {applicants[0]?.lastName}
                  </Text>
                </Box>
              )}

              {!isSms && (
                <Input
                  name="Subject"
                  spaceTop="15px"
                  placeholder="Enter the subject of your email"
                  required
                  label="Subject"
                  height="50px"
                  width="100%"
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setError({
                      ...error,
                      title: '',
                    });
                  }}
                  error={error?.title}
                />
              )}

              <Spacer space="15px" />
              <Box maxWidth="max">
                <Text uppercase style={{ fontWeight: '700' }}>
                  {isSms ? 'Message' : 'Body'}
                </Text>
                <Spacer space="10px" />
                {isSms ? (
                  <>
                    <TextArea
                      name="sms-message"
                      placeholder="Enter your SMS message"
                      block
                      height="140px"
                      value={body}
                      onChange={(e) => {
                        setBody(e.target.value);
                        setError({
                          ...error,
                          body: '',
                        });
                      }}
                      error={error?.body}
                    />
                    <Spacer space="6px" />
                    <Text size="12px" color={Theme.PrimaryGrey}>
                      Keep SMS short. Applicants without a phone number will be
                      skipped.
                    </Text>
                  </>
                ) : (
                  <RichTextEditor handleChange={setBody} value={body} />
                )}
              </Box>
            </Box>
          </Box>
          <ModalFooter>
            <Box width="fit-content" margin="0 0 0 auto" pad="30px 50px">
              <Button
                height={48}
                pale
                fontColor={Theme.PrimaryRed}
                color="transparent"
                spaceRight
                spaceLeft="auto"
                onClick={() => closeModal()}
              >
                cancel
              </Button>
              <Button
                iconRight={
                  <i
                    style={{ fontSize: '13px' }}
                    className={isSms ? 'saf-sms' : 'saf-mail'}
                  />
                }
                height={48}
                spaceLeft
                progress={sending}
                onClick={() => submit()}
                disabled={!canSubmit}
              >
                Send Notification
              </Button>
            </Box>
          </ModalFooter>
        </ModalBody>
      </Modal>
    </>
  );
};

SendMailModal.propTypes = {
  handleModal: PropTypes.func,
  emailState: PropTypes.object,
  application: PropTypes.object,
  callBack: PropTypes.func,
};
